<template>
	<div>
		<div class="row gutter-sm q-mb-lg">
			<div class="col-xs-12 col-sm-12">
				<q-field :count="256">
					<q-input
						v-model="title"
						ref="title"
						:float-label="$t('title')"
						:error="$v.title.$error"
						@input="$v.title.$touch"
						:disable="has_answers"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<p class="caption" :class="$v.description.$error ? 'text-negative' : ''">
					{{ $t('introduction') }}
				</p>

				<q-editor
					v-model="description"
					ref="description"
					:error="$v.description.$error"
					@input="$v.description.$touch"
					:disable="has_answers"
					:toolbar="[
						['bold', 'italic', 'strike', 'underline'],
						['left', 'center', 'right', 'justify'],
						['link', 'upload'],
						[
							{
								label: null,
								icon: 'fas fa-font',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
							},
							{
								label: null,
								icon: 'fas fa-text-height',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: [
									'size-1',
									'size-2',
									'size-3',
									'size-4',
									'size-5',
									'size-6',
									'size-7',
								],
							},
							'removeFormat',
						],
						['quote', 'unordered', 'ordered'],
					]"
					:definitions="{
						bold: { label: null, icon: 'fas fa-bold' },
						italic: { label: null, icon: 'fas fa-italic' },
						strike: { label: null, icon: 'fas fa-strikethrough' },
						underline: { label: null, icon: 'fas fa-underline' },
						left: { label: null, icon: 'fas fa-align-left' },
						center: { label: null, icon: 'fas fa-align-center' },
						right: { label: null, icon: 'fas fa-align-right' },
						justify: { label: null, icon: 'fas fa-align-justify' },
						link: { label: null, icon: 'fas fa-link' },
						removeFormat: { label: null, icon: 'fas fa-eraser' },
						quote: { label: null, icon: 'fas fa-quote-right' },
						unordered: { label: null, icon: 'fas fa-list-ul' },
						ordered: { label: null, icon: 'fas fa-list-ol' },
					}"
				>
				</q-editor>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-select
				v-model="is_mandatory"
				ref="is_mandatory"
				:float-label="$t('type')"
				:options="mandatory_list"
				:disable="has_answers"
				/>
			</div>
			<div class="col-xs-12 col-sm-6 row items-center">
				<q-checkbox
					class="q-mx-auto"
					:val="true"
					v-model="randomize_answers"
					:label="$t('randomize_answers')"
				/>
			</div>
		</div>

		<div class="col-xs-12 col-sm-12 q-mb-xl">
				<div class="row gutter-sm flex">
					<div class="col-xs-12 col-sm-6">
						<p class="caption">{{ $t('image') }}</p>
						<uploader
							content_type="images"
							@fileUploaded="imageUploaded"
							:multiple="false"
							:aspect_ratio="52 / 29"
							:aspect_ratio_string="'52/29'"
							:disable="has_answers"
						/>

						<p v-if="image_url" class="q-mt-md file-url">
							<q-icon name="fal fa-check" color="positive" />
							<a :href="image_url" target="_blank"> {{ $truncateLink(image_url) }}</a>
						</p>
					</div>
					<div class="col-xs-12 col-sm-6">
						<p class="caption">
							{{ $t('alt_image') }}
							<q-icon
								name="fal fa-question-circle"
								size="1.5rem"
								color="grey"
								class="q-ml-sm q-pb-sm"
							>
								<q-tooltip
									class="custom-tooltip"
									anchor="top left"
									self="bottom right"
								>
									{{ $t('alt_image_information_helper') }}
								</q-tooltip>
							</q-icon>
						</p>
						<div class="flex justify-between">
							<template v-for="lang in languages" class="row flex">
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'pt-br'">
									<q-input
										v-model="language_br"
										:float-label="$t(`${lang.name}`)"
										ref="language_br"
										:error="$v.language_br.$error"
										@input="$v.language_br.$touch"
										:disable="has_answers"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'en-us'">
									<q-input
										v-model="language_us"
										:float-label="$t(`${lang.name}`)"
										ref="language_us"
										:error="$v.language_us.$error"
										@input="$v.language_us.$touch"
										:disable="has_answers"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'es-es'">
									<q-input
										v-model="language_es"
										:float-label="$t(`${lang.name}`)"
										ref="language_es"
										:error="$v.language_es.$error"
										@input="$v.language_es.$touch"
										:disable="has_answers"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'fr-fr'">
									<q-input
										v-model="language_fr"
										:float-label="$t(`${lang.name}`)"
										ref="language_fr"
										:error="$v.language_fr.$error"
										@input="$v.language_fr.$touch"
										:disable="has_answers"
									/>
								</q-field>
							</template>
						</div>
					</div>
				</div>
			</div>

		<q-tabs v-model="tab" color="secondary" class="shadow-1">
			<q-tab
				slot="title"
				name="permissions"
				icon="fal fa-clipboard-list-check"
				:label="$t('permissions')"
			/>

			<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('areas') }}</p>
						<q-checkbox
							class="q-mb-sm"
							v-model="all_areas"
							:val="true"
							:label="$t('select_all_areas')"
							@input="checkAllAreas()"
							:disable="has_answers"
						/>
						<q-select
							filter
							autofocus-filter
							v-model="areas"
							ref="areas"
							:options="areas_list"
							:error="$v.areas.$error"
							@input="$v.areas.$touch"
							multiple
							chips
							:disable="has_answers"
						/>
					</div>

					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									v-model="all_profiles"
									:val="true"
									:label="$t('all')"
									@input="checkAll()"
									:disable="has_answers"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in profiles_list"
								:key="index"
								:class="getErrorClass($v.profiles.$error)"
							>
								<q-checkbox
									keep-color
									v-model="profiles"
									ref="profiles"
									:val="item.value"
									:label="item.label"
									@input="$v.profiles.$touch"
									:color="getErrorColor($v.profiles.$error)"
									:disable="has_answers"
								/>
							</div>

							<div class="col-xs-12 col-sm-12">
								<q-select
									v-model="user_groups"
									ref="user_groups"
									:options="user_groups_list"
									:float-label="$t('groups')"
									:error="$v.user_groups.$error"
									@input="$v.user_groups.$touch"
									multiple
									chips
									:disable="has_answers"
								/>
							</div>
						</div>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'admin_drills' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Uploader from 'components/Uploader'
import { mapGetters } from 'vuex'
import { required, requiredIf, maxValue, minValue, maxLength } from 'vuelidate/lib/validators'
import { date } from 'quasar'
const { formatDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
	i18nOptions: { namespaces: ['custom', 'drill', 'admin', 'common'] },
	data() {
		return {
			language_br: '',
			language_us: '',
			language_es: '',
			language_fr: '',
			drill_id: 0,
			areas: [],
			description: '',
			end_date: null,
			profiles: [],
			score: 0,
			start_date: null,
			title: '',
			user_groups: [],
			tab: 'permissions',
			status: 2,
			all_profiles: false,
			selected_client_params: [],
			all_areas: false,
			selected_country: null,
			image_url: '',
			minimum_score: 0,
			is_mandatory: false,
			mandatory_list: [
				{
					label: this.$t('mandatory'),
					value: true
				},
				{
					label: this.$t('optional'),
					value: false
				}
			],
			date_type: 1,
			qty_questions_frequency: 1,
			time_to_answer: 0,
			drill_qty_questions: 0,
			show_feedback: true,
			allow_approved_to_answer: false,
			has_answers: false,
			custom_dates: [],
			drill_dates: [],
			randomize_answers: true,
		}
	},

	components: {
		uploader: Uploader,
		'date-picker': DatePicker
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			areas_list: 'registration_lists/country_areas',
			user_groups_list: 'registration_lists/group_users',
			profiles_list: 'registration_lists/profiles',
			user_id: 'common/id',
			tags: 'registration_lists/tags',
			user_full_name: 'common/full_name',
			user_email: 'common/email',
			languages: 'params/languages',
			countries: 'registration_lists/countries',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		getColorStatus() {
			if (this.status == 1) return 'positive'
			else if (this.status == 2) return 'negative'
			else return 'warning'
		},
	},

	methods: {
		fetchData() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_drills/drills/${this.drill_id}/`, this.getHeader)
				.then((response) => {
					this.areas = response.data.areas
					this.description = response.data.description
					this.drill_dates = response.data.drill_dates
					this.profiles = response.data.profiles
					this.title = response.data.title
					this.user_groups = response.data.user_groups
					this.score = response.data.score
					this.minimum_score = response.data.minimum_score
					this.is_mandatory = response.data.is_mandatory
					this.status = response.data.status
					this.qty_questions_frequency = response.data.qty_questions_frequency
					this.image_url = response.data.drill_images.length ? response.data.drill_images[0].image : ''
					this.drill_qty_questions = response.data.drill_qty_questions
					this.time_to_answer = date.buildDate({
								hours: this.getSecondsToTime(response.data.time_to_answer).split(':')[0],
								minutes: this.getSecondsToTime(response.data.time_to_answer).split(':')[1],
							})
					if (response.data.drill_image_alt_text.length) {
						this.language_br = response.data.drill_image_alt_text.find(alt => alt.language == 'pt-br').alt_text
						this.language_us = response.data.drill_image_alt_text.find(alt => alt.language == 'en-us').alt_text
						this.language_es = response.data.drill_image_alt_text.find(alt => alt.language == 'es-es').alt_text
						this.language_fr = response.data.drill_image_alt_text.find(alt => alt.language == 'fr-fr').alt_text
					}
					this.show_feedback = response.data.show_feedback
					this.allow_approved_to_answer = response.data.allow_approved_to_answer
					this.has_answers = response.data.has_answers
					this.date_type = response.data.date_type
					this.randomize_answers = response.data.randomize_answers
					this.$emit('set_has_answers', this.has_answers)
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				const data = {
					title: this.title,
					description: this.description,
					drill_dates: this.drill_dates,
					areas: this.areas,
					profiles: this.profiles,
					user_groups: this.user_groups,
					country: this.selected_country.value,
					client: this.selected_client_params.id,
					is_mandatory: this.is_mandatory,
					status: this.status,
					drill_images: this.image_url,
					score: this.score,
					minimum_score: this.minimum_score,
					qty_questions_frequency: this.qty_questions_frequency,
					time_to_answer:  this.time_to_answer ? this.getTimeToSeconds(this.time_to_answer) : null,
					drill_image_alt_texts: {
						'pt-br': this.language_br ? this.language_br : '',
						'en-us': this.language_us ? this.language_us : '',
						'es-es': this.language_es ? this.language_es : '',
						'fr-fr': this.language_fr ? this.language_fr : '',
					},
					date_type: this.date_type,
					show_feedback: this.show_feedback,
					allow_approved_to_answer: this.allow_approved_to_answer,
					randomize_answers: this.randomize_answers,
				}
				

				if (this.drill_id != 0) {
					this.$axios
						.put(`/admin_drills/drills/${this.drill_id}/`, data, this.getHeader)
						.then((response) => {
							this.$q.notify({
								message: this.$t('drill_save_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})
						})
						.catch((e) => {
							console.log(e)

							this.$q.notify({
								message: this.$t('drill_save_error'),
								type: 'negative',
								icon: 'fal fa-ban',
							})
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				} else {
					this.$axios
						.post(`/admin_drills/drills/`, data, this.getHeader)
						.then((response) => {
							this.drill_id = response.data.id
							this.$router.push({
								name: 'admin_drill',
								params: { id: response.data.id },
							})

							this.$q.notify({
								message: this.$t('drill_save_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})
						})
						.catch((e) => {
							console.log(e)

							this.toastError('drill_save_error')

						})
						.finally(() => {
							this.$q.loading.hide()
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)
				if (this.$v.score.maxValue) {
					this.toastError('make_sure_that_all_fields_are_filled_in')
				}
			}
		},

		toastError(value) {
			this.$q.notify({
				message: this.$t(value),
				type: 'negative',
				icon: 'fal fa-exclamation-triangle',
				position: 'top',
			})
		},

		getSecondsToTime(seconds) {
			let hours = Math.floor(seconds / 3600)
			let minutes = Math.floor((seconds % 3600) / 60)

			return `${hours}:${minutes}`
		},

		checkAll() {
			if (this.all_profiles) {
				this.profiles_list.forEach((element) => {
					this.profiles.push(element.value)
				})
			} else {
				this.profiles = []
			}
		},

		checkAllAreas() {
			if (this.all_areas) {
				this.areas = this.areas_list.map((e) => e.value)
			} else {
				this.areas = []
			}
		},

		getErrorColor(state) {
			if (state) return 'negative'
			else return ''
		},

		getErrorClass(state) {
			if (state) return 'checkbox-error'
			else return ''
		},

		getTimeToSeconds(time) {
			let dateTime = new Date(time)

			let hours = dateTime.getHours()
			let minutes = dateTime.getMinutes()

			return hours * 3600 + minutes * 60
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		imageUploaded(url) {
			this.image_url = url
		},
	},

	created() {
		this.drill_id = this.$route.params.id
		if (this.drill_id != 0) this.fetchData()

		let client = this.clients.find((obj) => {
			return obj.id === this.id_user_client_selected
		})
		this.selected_client_params = client ? client : {}

		this.selected_country = this.countries.find(c => c.value == this.id_user_client_country_selected)
	},

	validations() {
		return {
			title: { required, maxLength: maxLength(256) },
			description: { required },
			language_br: { maxLength: maxLength(170) },
			language_us: { maxLength: maxLength(170) },
			language_es: { maxLength: maxLength(170) },
			language_fr: { maxLength: maxLength(170) },
			areas: {
				requiredIf: requiredIf(() => {
					if (this.profiles.length == 0 && this.areas.length == 0) {
						if (
							this.profiles.length == 0 &&
							this.areas.length == 0 &&
							this.user_groups.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			profiles: {
				requiredIf: requiredIf(() => {
					if (this.profiles.length == 0 && this.areas.length == 0) {
						if (
							this.profiles.length == 0 &&
							this.areas.length == 0 &&
							this.user_groups.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			user_groups: {
				requiredIf: requiredIf(() => {
					if (
						this.profiles.length == 0 &&
						this.areas.length == 0 &&
						this.user_groups.length == 0
					) {
						return true
					} else {
						return false
					}
				}),
			},
		}
	},
}
</script>

<style></style>
